"use client";

import { c as _c } from "react/compiler-runtime";
import { useId, useState } from "react";
import { Typography } from "@repo/ui/aops-components/typography";
import { Accordion } from "@repo/ui/mantine";
import { Minus, Plus } from "lucide-react";
import { useAtom } from "jotai";
import { cn } from "@repo/ui/lib/utils";
import { useIsMobile } from "@repo/ui/hooks/use-mobile";
import { learningFormatAtom, selectedCampusAtom } from "@/app/atoms/courseSelections";
import sectionStyles from "@/app/components/CourseSection/CourseSection.module.css";
import { CourseTag } from "@/app/components/CoursePage/CourseTag/CourseTag";
import { SelectableTile } from "../SelectableTile";
import styles from "./LearningFormat.module.css";
import { LEARN_MORE_VALUE, FALLBACK_SUBTITLE, FALLBACK_TITLE, LEARN_MORE_KEY, FALLBACK_LEARN_MORE_TITLE, WHY_CHOOSE_KEY, FALLBACK_WHY_CHOOSE_TITLE, MODALITY_TITLE_PLACEHOLDER, HIDE_DETAILS_KEY, FALLBACK_HIDE_DETAILS_TEXT, LearningFormatBestFor, LearningFormatDetailRow, LearningFormatHeader, LearningFormatQuickFact, LearningFormatWhyChoose, StickyFormatChrome, isBeastAcademyModality, useModalityRows, type LearningFormatProps, type ModalityRow, STEP_TAG_KEY, FALLBACK_STEP_TAG } from "./LearningFormatParts";
import type { CoursePageSection } from "@repo/sanity-utils/query-types";
import { getKeyedCopyText } from "@repo/utils/keyedCopy";
import { sendDiscoveryEventBeacon } from "@/lib/discoveryEventBeacon";
import { didOpenLearnMore, getLearningFormatSectionCopy } from "../LearningFormatSelectionUtils";
type MobileOptionProps = {
  row: ModalityRow;
  courseSlug: string;
  onSelect: (modality: ModalityRow["modality"]) => void;
  coursePageSection: CoursePageSection;
  linkToBAO: () => void;
  initialLearnMoreOpen: boolean;
};
function MobileFormatOption(t0) {
  const $ = _c(58);
  const {
    row,
    courseSlug,
    onSelect,
    coursePageSection,
    linkToBAO,
    initialLearnMoreOpen
  } = t0;
  const {
    modality,
    modalityOffering,
    priceLines,
    quickFacts,
    isSelectDisabled,
    isSelected
  } = row;
  let t1;
  if ($[0] !== initialLearnMoreOpen) {
    t1 = () => initialLearnMoreOpen ? LEARN_MORE_VALUE : null;
    $[0] = initialLearnMoreOpen;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [openedAccordion, setOpenedAccordion] = useState(t1);
  const isLearnMoreOpen = openedAccordion === LEARN_MORE_VALUE;
  const isPhone = useIsMobile() === true;
  const hasWhyChoose = (modality.whyChoose?.length ?? 0) > 0;
  const hasBestFor = (modality.bestFor?.length ?? 0) > 0;
  const hasDetails = hasWhyChoose || hasBestFor || quickFacts.length > 0;
  let keyedCopy;
  let learnMoreText;
  let t2;
  if ($[2] !== coursePageSection?.keyedCopy || $[3] !== modality.title) {
    keyedCopy = coursePageSection?.keyedCopy ?? [];
    learnMoreText = getKeyedCopyText(keyedCopy ?? [], LEARN_MORE_KEY) || FALLBACK_LEARN_MORE_TITLE;
    t2 = (getKeyedCopyText(keyedCopy ?? [], WHY_CHOOSE_KEY) || FALLBACK_WHY_CHOOSE_TITLE).replace(MODALITY_TITLE_PLACEHOLDER, modality.title);
    $[2] = coursePageSection?.keyedCopy;
    $[3] = modality.title;
    $[4] = keyedCopy;
    $[5] = learnMoreText;
    $[6] = t2;
  } else {
    keyedCopy = $[4];
    learnMoreText = $[5];
    t2 = $[6];
  }
  const whyChooseText = t2;
  let t3;
  if ($[7] !== keyedCopy) {
    t3 = getKeyedCopyText(keyedCopy ?? [], HIDE_DETAILS_KEY) || FALLBACK_HIDE_DETAILS_TEXT;
    $[7] = keyedCopy;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const hideDetailsText = t3;
  const collapsedLabel = isPhone && modality.title ? whyChooseText : learnMoreText;
  let t4;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = cn(sectionStyles.optionCard, styles.mobileOptionCard);
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const t5 = isSelectDisabled || undefined;
  let t6;
  if ($[10] !== courseSlug || $[11] !== isSelectDisabled || $[12] !== isSelected || $[13] !== linkToBAO || $[14] !== modality || $[15] !== modalityOffering || $[16] !== onSelect || $[17] !== priceLines) {
    t6 = isStuck => <><SelectableTile.Content className={styles.formatHeaderContent}><LearningFormatHeader modality={modality} modalityOffering={modalityOffering} priceLines={priceLines} isCompact={isStuck} /></SelectableTile.Content><SelectableTile.SelectButton isSelected={isSelected} disabled={isSelectDisabled} onClick={() => {
        sendDiscoveryEventBeacon("course_modality_selected", {
          course: courseSlug,
          modality: modality.slug.current,
          auto_select: false
        });
        if (isBeastAcademyModality(modality)) {
          linkToBAO();
        }
        onSelect(modality);
      }} /></>;
    $[10] = courseSlug;
    $[11] = isSelectDisabled;
    $[12] = isSelected;
    $[13] = linkToBAO;
    $[14] = modality;
    $[15] = modalityOffering;
    $[16] = onSelect;
    $[17] = priceLines;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== isLearnMoreOpen || $[20] !== t6) {
    t7 = <StickyFormatChrome stickyEnabled={isLearnMoreOpen}>{t6}</StickyFormatChrome>;
    $[19] = isLearnMoreOpen;
    $[20] = t6;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== hasWhyChoose || $[23] !== isLearnMoreOpen || $[24] !== keyedCopy || $[25] !== modality) {
    t8 = hasWhyChoose && <><SelectableTile.Content className={cn(styles.whyChooseTablet, isLearnMoreOpen && styles.formatWhyChoose)}><LearningFormatWhyChoose modality={modality} keyedCopy={keyedCopy} />{modality.modalityFootnote && <Typography variant="bodySmall" className={styles.modalityFootnote}>{modality.modalityFootnote}</Typography>}</SelectableTile.Content><div className={styles.whyChoosePhone}><LearningFormatDetailRow expanded={isLearnMoreOpen}><LearningFormatWhyChoose modality={modality} keyedCopy={keyedCopy} />{modality.modalityFootnote && <Typography variant="bodySmall" className={styles.modalityFootnote}>{modality.modalityFootnote}</Typography>}</LearningFormatDetailRow></div></>;
    $[22] = hasWhyChoose;
    $[23] = isLearnMoreOpen;
    $[24] = keyedCopy;
    $[25] = modality;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  let t9;
  if ($[27] !== hasBestFor || $[28] !== isLearnMoreOpen || $[29] !== keyedCopy || $[30] !== modality) {
    t9 = hasBestFor && <LearningFormatDetailRow expanded={isLearnMoreOpen}><LearningFormatBestFor modality={modality} keyedCopy={keyedCopy} /></LearningFormatDetailRow>;
    $[27] = hasBestFor;
    $[28] = isLearnMoreOpen;
    $[29] = keyedCopy;
    $[30] = modality;
    $[31] = t9;
  } else {
    t9 = $[31];
  }
  let t10;
  if ($[32] !== isLearnMoreOpen || $[33] !== quickFacts) {
    let t11;
    if ($[35] !== isLearnMoreOpen) {
      t11 = fact => <LearningFormatDetailRow key={fact._key} expanded={isLearnMoreOpen}><LearningFormatQuickFact fact={fact} /></LearningFormatDetailRow>;
      $[35] = isLearnMoreOpen;
      $[36] = t11;
    } else {
      t11 = $[36];
    }
    t10 = quickFacts.map(t11);
    $[32] = isLearnMoreOpen;
    $[33] = quickFacts;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  let t11;
  if ($[37] !== isSelectDisabled || $[38] !== isSelected || $[39] !== t10 || $[40] !== t7 || $[41] !== t8 || $[42] !== t9) {
    t11 = <div className={sectionStyles.optionCardDescription}><SelectableTile isSelected={isSelected} disabled={isSelectDisabled}>{t7}{t8}{t9}{t10}</SelectableTile></div>;
    $[37] = isSelectDisabled;
    $[38] = isSelected;
    $[39] = t10;
    $[40] = t7;
    $[41] = t8;
    $[42] = t9;
    $[43] = t11;
  } else {
    t11 = $[43];
  }
  let t12;
  if ($[44] !== t11 || $[45] !== t5) {
    t12 = <div className={styles.mobileCardFrame}><div className={t4} data-disabled={t5}>{t11}</div></div>;
    $[44] = t11;
    $[45] = t5;
    $[46] = t12;
  } else {
    t12 = $[46];
  }
  let t13;
  if ($[47] !== collapsedLabel || $[48] !== courseSlug || $[49] !== hasDetails || $[50] !== hideDetailsText || $[51] !== isLearnMoreOpen || $[52] !== modality.slug.current || $[53] !== openedAccordion) {
    t13 = hasDetails && <Accordion variant="unstyled" value={openedAccordion} onChange={value => {
      if (didOpenLearnMore(value, openedAccordion, LEARN_MORE_VALUE)) {
        sendDiscoveryEventBeacon("course_modality_details_viewed", {
          course: courseSlug,
          modality: modality.slug.current
        });
      }
      setOpenedAccordion(value);
    }} chevron={null} classNames={{
      root: styles.mobileAccordionRoot,
      item: styles.mobileAccordionItem,
      control: styles.mobileAccordionControl,
      label: styles.accordionLabel
    }}><Accordion.Item value={LEARN_MORE_VALUE}><Accordion.Control onClick={_temp}><span className={styles.accordionControlInner}><Typography variant="buttonMd" as="span">{isLearnMoreOpen ? hideDetailsText : collapsedLabel}</Typography>{isLearnMoreOpen ? <Minus size={24} strokeWidth={1.5} aria-hidden={true} /> : <Plus size={24} strokeWidth={1.5} aria-hidden={true} />}</span></Accordion.Control></Accordion.Item></Accordion>;
    $[47] = collapsedLabel;
    $[48] = courseSlug;
    $[49] = hasDetails;
    $[50] = hideDetailsText;
    $[51] = isLearnMoreOpen;
    $[52] = modality.slug.current;
    $[53] = openedAccordion;
    $[54] = t13;
  } else {
    t13 = $[54];
  }
  let t14;
  if ($[55] !== t12 || $[56] !== t13) {
    t14 = <div className={styles.mobileOption}>{t12}{t13}</div>;
    $[55] = t12;
    $[56] = t13;
    $[57] = t14;
  } else {
    t14 = $[57];
  }
  return t14;
}

/**
 * Single-column Learning Format for tablet/phone (&lt;1024px). Each option owns
 * its own Learn more accordion. On phone (&lt;768px), whyChoose moves into the
 * detail rows via Collapse (see `.whyChooseTablet` / `.whyChoosePhone` CSS).
 */
function _temp(e) {
  e.stopPropagation();
}
export function LearningFormatMobile(t0) {
  const $ = _c(42);
  const {
    campuses,
    campusCoursePrices,
    course,
    coursePageSection,
    campusLocationCoursePageSection,
    linkToBAO,
    onSelectLearningFormat,
    initialLearnMoreOpen: t1
  } = t0;
  const initialLearnMoreOpen = t1 === undefined ? false : t1;
  const titleId = useId();
  const [selectedLearningFormat] = useAtom(learningFormatAtom);
  const [selectedCampusId] = useAtom(selectedCampusAtom);
  let t2;
  if ($[0] !== coursePageSection?.keyedCopy) {
    t2 = coursePageSection?.keyedCopy ?? [];
    $[0] = coursePageSection?.keyedCopy;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const modalityRows = useModalityRows(course, selectedLearningFormat, campuses, selectedCampusId, campusCoursePrices, t2, campusLocationCoursePageSection);
  const t3 = coursePageSection?.title;
  const t4 = coursePageSection?.body;
  let t5;
  if ($[2] !== coursePageSection?.keyedCopy || $[3] !== modalityRows[0]?.modality.slug.current || $[4] !== modalityRows.length || $[5] !== selectedCampusId || $[6] !== t3 || $[7] !== t4) {
    t5 = getLearningFormatSectionCopy({
      sectionTitle: t3,
      sectionBody: t4,
      keyedCopy: coursePageSection?.keyedCopy ?? [],
      modalityCount: modalityRows.length,
      modalitySlug: modalityRows[0]?.modality.slug.current ?? undefined,
      selectedCampusId,
      defaultTitle: FALLBACK_TITLE,
      defaultSubtitle: FALLBACK_SUBTITLE
    });
    $[2] = coursePageSection?.keyedCopy;
    $[3] = modalityRows[0]?.modality.slug.current;
    $[4] = modalityRows.length;
    $[5] = selectedCampusId;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const {
    title,
    subtitle
  } = t5;
  let t6;
  if ($[9] !== coursePageSection?.keyedCopy) {
    t6 = getKeyedCopyText(coursePageSection?.keyedCopy ?? [], STEP_TAG_KEY) || FALLBACK_STEP_TAG;
    $[9] = coursePageSection?.keyedCopy;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const stepTag = t6;
  if (!modalityRows.length) {
    return null;
  }
  let t7;
  if ($[11] !== stepTag) {
    t7 = <CourseTag>{stepTag}</CourseTag>;
    $[11] = stepTag;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== title || $[14] !== titleId) {
    t8 = <Typography id={titleId} as="h2" variant="h4">{title}</Typography>;
    $[13] = title;
    $[14] = titleId;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== subtitle) {
    t9 = <Typography variant="body">{subtitle}</Typography>;
    $[16] = subtitle;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t7 || $[19] !== t8 || $[20] !== t9) {
    t10 = <div className={sectionStyles.header}>{t7}{t8}{t9}</div>;
    $[18] = t7;
    $[19] = t8;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== course?.slug || $[23] !== coursePageSection || $[24] !== initialLearnMoreOpen || $[25] !== linkToBAO || $[26] !== modalityRows || $[27] !== onSelectLearningFormat) {
    let t12;
    if ($[29] !== course?.slug || $[30] !== coursePageSection || $[31] !== initialLearnMoreOpen || $[32] !== linkToBAO || $[33] !== onSelectLearningFormat) {
      t12 = row => <MobileFormatOption key={row.modality._id} row={row} courseSlug={course?.slug.current ?? ""} onSelect={onSelectLearningFormat} coursePageSection={coursePageSection ?? null} linkToBAO={linkToBAO} initialLearnMoreOpen={initialLearnMoreOpen} />;
      $[29] = course?.slug;
      $[30] = coursePageSection;
      $[31] = initialLearnMoreOpen;
      $[32] = linkToBAO;
      $[33] = onSelectLearningFormat;
      $[34] = t12;
    } else {
      t12 = $[34];
    }
    t11 = modalityRows.map(t12);
    $[22] = course?.slug;
    $[23] = coursePageSection;
    $[24] = initialLearnMoreOpen;
    $[25] = linkToBAO;
    $[26] = modalityRows;
    $[27] = onSelectLearningFormat;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[35] !== t11) {
    t12 = <div className={styles.mobileBody}>{t11}</div>;
    $[35] = t11;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  let t13;
  if ($[37] !== t10 || $[38] !== t12 || $[39] !== title || $[40] !== titleId) {
    t13 = <section id="formats" className={sectionStyles.section} aria-label={title} aria-labelledby={titleId}>{t10}{t12}</section>;
    $[37] = t10;
    $[38] = t12;
    $[39] = title;
    $[40] = titleId;
    $[41] = t13;
  } else {
    t13 = $[41];
  }
  return t13;
}